import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { EMPTY, catchError, from, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SessionService } from '../services/session.service';
import { HAPPEN_AUTH_CONFIG } from '../happen-auth.config';

export interface NormalizedAuthError {
  slug: string;
  message: string;
  status: number;
  type?: string;
  state?: string;
  gate?: 'provider' | 'platform-native-mfa';
  tenantId?: string;
  entryContinuationId?: string;
  applicationId?: string;
  unlockToken?: string;
  recoveryCodesRemaining?: number;
  selfServeDisabled: boolean;
}

/**
 * Maps backend RFC 7807 ProblemDetails responses to typed UI error objects.
 * Handles 401 responses by attempting a silent token refresh (AC-1.4.1, AC-1.4.2).
 * Registered as the SECOND interceptor in the chain (after authInterceptor).
 */
export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionService = inject(SessionService);
  // AuthService is injected here (not in authInterceptor) to call handleSessionExpiry(),
  // which also clears _currentUser so authService.isAuthenticated() reflects reality after expiry.
  // Functional interceptors resolve inject() lazily at request time — AuthService is already
  // instantiated by then, so no circular DI issue with AuthService → HttpClient → interceptors.
  const authService = inject(AuthService);
  const config = inject(HAPPEN_AUTH_CONFIG);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      const problemDetails = error.error as { type?: string; title?: string; status?: number } | null;

      // Exclude token refresh and sign-out endpoints from 401 handling to prevent
      // re-entrant loops and interference with best-effort sign-out.
      const isRefreshEndpoint = req.url.includes('/api/v1/auth/token');
      const isSignOutEndpoint = req.url.includes('/api/v1/auth/signout');
      // Session cookie endpoints use their own auth (not Bearer JWT) — 401 from these
      // means the auth-server session cookie is missing/expired, not a JWT issue.
      // Do not attempt JWT refresh for these; let the error propagate to the caller.
      const isSessionCookieEndpoint = req.url.includes('/session/user-sessions');
      const requestUrl = req.url.split('?')[0].split('#')[0];
      const applicationId = (req.body as { applicationId?: unknown } | null)?.applicationId;
      const errorSlug = problemDetails?.type?.split('/errors/')[1];
      const isPayrollEntryRefusal = req.method === 'POST'
        && typeof applicationId === 'string' && applicationId.toLowerCase() === 'happen-payroll'
        && (requestUrl === `${config.apiBaseUrl}/session/admit`
          || requestUrl === `${config.apiBaseUrl}/api/v1/auth/sso/select-tenant`)
        && (errorSlug === 'step_up_required' || errorSlug === 'entry_continuation_unbound'
          || errorSlug === 'entry_continuation_expired' || errorSlug === 'entry_continuation_mismatch');
      // Private BFF routes authenticate with the protected BFF cookie. Their 401 means that cookie
      // is stale or absent; refreshing the SPA Bearer token cannot repair it and can mask the error
      // from staff UI callers by retrying or completing with EMPTY.
      const isBffEndpoint = req.url.includes('/bff/');
      const isLockoutHandoff = /\/api\/v1\/auth\/authorize\/lockout-handoff(?:\/unlock|\/cancel)?$/.test(
        req.url.split('?')[0].split('#')[0]);

      if (error.status === 401 && !isRefreshEndpoint && !isSignOutEndpoint
          && !isSessionCookieEndpoint && !isPayrollEntryRefusal && !isBffEndpoint && !isLockoutHandoff) {
        // Session-expired ProblemDetails type means idle or absolute timeout — skip refresh,
        // navigate directly to the session-expired screen.
        if (problemDetails?.type === 'https://happen.biz/errors/session-expired') {
          authService.handleSessionExpiry('expired');
          return EMPTY;
        }

        // Silent token refresh only makes sense for a genuine token-expiry 401 during an
        // active session. If there is no session token, the 401 is an auth decision made
        // before any session exists (e.g. no-account-found / account-deactivated during
        // Microsoft SSO sign-in — Story 1.3 AC-1.3.8 / AC-1.3.9). Attempting a refresh
        // there fails and wrongly routes to the session-expired screen; instead, let the
        // error propagate so AuthService._mapError can surface the correct message.
        const hasSession = sessionService.getAccessToken() !== null;
        if (hasSession) {
          return from(sessionService.refreshTokensAsync()).pipe(
            switchMap((outcome) => {
              if (outcome === 'refreshed') {
                // Refresh succeeded — retry the original request with the new token.
                const newToken = sessionService.getAccessToken()!;
                const retryReq = req.clone({
                  setHeaders: { Authorization: `Bearer ${newToken}` },
                });
                return next(retryReq);
              }
              if (outcome === 'throttled') {
                // cc-16b (AC-CC16b.5): the refresh was rate-limited (429). The session is
                // INTACT — one request was refused. Signing the user out here would tell them
                // something false, so handleSessionExpiry is deliberately NOT called and there
                // is no navigation.
                //
                // Rethrow the ORIGINAL HttpErrorResponse rather than returning EMPTY. EMPTY
                // would complete the caller's observable with no value, which a calling
                // component reads as an empty success — a worse failure than a visible error.
                return throwError(() => error);
              }
              // 'unrecoverable' — session cannot be recovered, show the session-expired screen.
              authService.handleSessionExpiry('expired');
              return EMPTY;
            })
          );
        }
        // No session — fall through to the typed-error mapping below.
      }

      const extendedDetails = error.error as {
        type?: string; title?: string; status?: number; state?: string;
        gate?: string; tenantId?: string; entryContinuationId?: string; applicationId?: string;
        unlockToken?: string; recoveryCodesRemaining?: number;
        selfServeDisabled?: boolean;
      } | null;
      const normalized: NormalizedAuthError = {
        slug: problemDetails?.type?.split('/errors/')[1] ?? 'unknown-error',
        message: problemDetails?.title ?? 'An unexpected error occurred.',
        status: error.status,
        type: typeof extendedDetails?.type === 'string' ? extendedDetails.type : undefined,
        state: typeof extendedDetails?.state === 'string' ? extendedDetails.state : undefined,
        gate: extendedDetails?.gate === 'provider' || extendedDetails?.gate === 'platform-native-mfa'
          ? extendedDetails.gate : undefined,
        tenantId: typeof extendedDetails?.tenantId === 'string' ? extendedDetails.tenantId : undefined,
        entryContinuationId: typeof extendedDetails?.entryContinuationId === 'string' ? extendedDetails.entryContinuationId : undefined,
        applicationId: typeof extendedDetails?.applicationId === 'string' ? extendedDetails.applicationId : undefined,
        unlockToken: extendedDetails?.unlockToken,
        recoveryCodesRemaining: extendedDetails?.recoveryCodesRemaining,
        selfServeDisabled: extendedDetails?.selfServeDisabled ?? false,
      };
      return throwError(() => normalized);
    })
  );
};
